import { useInitializationDataContext } from '../../../context/data-context';
import { ErrorResponse, ErrorType } from '../../../global-types';
import { AddressForm, AddressRecipientUpdate } from '../../../global-types/addresses';
import { useSystemErrorHandling } from '../../../hooks';
import useAddressSelection from '../../../hooks/use-address-selection';
import { updateAddress } from '../../../services/address';
import { handleErrorValidation } from '../utils/handle-error-validation';
import { AddAddressToValidate } from './use-address-confirmation-dialog';

export type UseUpdateOneToManyAddressesProps = {
  addAddressToValidate: AddAddressToValidate;
  openAddressConfirmationDialog: () => void;
  closeAddressConfirmationDialog: () => void;
};

export const useUpdateOneToManyAddresses = ({
  addAddressToValidate,
  openAddressConfirmationDialog,
  closeAddressConfirmationDialog,
}: UseUpdateOneToManyAddressesProps) => {
  // contexts
  const { handleSetSender, handleUpdateRecipientContact, isRecipientAddress } = useAddressSelection();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  // hooks
  const { onSystemError } = useSystemErrorHandling();

  const projectId = initializedData?.project_id;
  const accountId = initializedData?.account_id;

  const updateSenderAddress = (addressForm: AddressForm, resetForm: () => void) => {
    if (!projectId || !addressForm.address_id) {
      return;
    }

    updateAddress(projectId, addressForm.address_id, mapContactToAddressRecipientUpdate(addressForm))
      .then((response) => {
        if (response.data) {
          const data = response.data;
          handleSetSender({
            sender: {
              ...addressForm,
              account_id: accountId || '',
              address_hash_id: data.address_hash_id || '',
              contact_id: data.address_id,
              contact_type_code: data.address_type_code,
              is_verified: data.is_verified,
              created_at: data.created_at,
              last_updated_at: data.last_updated_at,
            },
            enableRecipientAddresses: true,
          });

          resetForm();
          closeAddressConfirmationDialog();
        }
      })
      .catch((error: ErrorResponse) => {
        // check if any of the errors are address errors
        const allErrors = error.data.errors.map((error) => error.description as ErrorType);

        // if there are any address errors, set the address to validate
        if (error.status === 422 && allErrors.length > 0) {
          handleErrorValidation(allErrors, addAddressToValidate, addressForm, isRecipientAddress);
          openAddressConfirmationDialog();
          return;
        }

        // if the error is not an address error, throw a system error
        onSystemError(error);
      });
  };

  const updateRecipientAddress = (addressForm: AddressForm, resetForm: () => void) => {
    if (!projectId || !addressForm.address_id) {
      return;
    }

    updateAddress(projectId, addressForm.address_id, mapContactToAddressRecipientUpdate(addressForm))
      .then((response) => {
        if (response.data) {
          const data = response.data;
          void handleUpdateRecipientContact({
            address_hash_id: data.address_hash_id || '',
            account_id: accountId || '',
            address_line_1: data.address_line_1,
            address_line_2: data.address_line_2,
            city: data.city,
            company_name: data.company_name,
            contact_id: data.address_id,
            contact_type_code: data.address_type_code,
            country_code: data.country_code,
            created_at: data.created_at,
            first_name: data.first_name,
            is_verified: data.is_verified,
            last_name: data.last_name,
            last_updated_at: data.last_updated_at,
            state_code: data.state_code,
            zip: data.zip,
          });

          resetForm();
          closeAddressConfirmationDialog();
        }
      })
      .catch((error: ErrorResponse) => {
        // check if any of the errors are address errors
        const allErrors = error.data.errors.map((error) => error.description as ErrorType);

        // if there are any address errors, set the address to validate
        if (error.status === 422 && allErrors.length > 0) {
          handleErrorValidation(allErrors, addAddressToValidate, addressForm, isRecipientAddress);
          openAddressConfirmationDialog();
          return;
        }

        // if the error is not an address error, throw a system error
        onSystemError(error);
      });
  };

  return { updateSenderAddress, updateRecipientAddress };
};

const mapContactToAddressRecipientUpdate = (addressForm: AddressForm): AddressRecipientUpdate => {
  return {
    company_name: addressForm.company_name,
    first_name: addressForm.first_name,
    last_name: addressForm.last_name,
    address_line_1: addressForm.address_line_1,
    address_line_2: addressForm.address_line_2,
    city: addressForm.city,
    state_code: addressForm.state_code || '',
    zip: addressForm.zip,
    country_code: addressForm.country_code,
    skip_usps_validation: addressForm.skip_usps_validation,
  };
};
